/**
 * Tenant context store — F-024.
 *
 * The active tenant id is persisted to localStorage so a returning user lands
 * on the same tenant they were last working in. `setTenants` is the
 * reconciler: whichever route loader fetches the user's tenant list calls it
 * after auth, and if the persisted activeTenantId is no longer valid
 * (revoked access, deleted firm), it falls back to the first tenant in the
 * list.
 *
 * `switchTenant` POSTs to /api/v1/auth/switch-tenant, which re-stamps the
 * claim (app_metadata.tenant_id) server side; the client then refreshes the
 * session to mint a token carrying it. If the POST fails, nothing moved and
 * the local activeTenantId is left unchanged. If the REFRESH fails, the claim
 * did move, so it is stamped back to the previous tenant, and if that rollback
 * fails too the session is torn down rather than left pointing one way while
 * the UI points another.
 *
 * Backend endpoint may not exist yet (Phase 10/11) — the failure mode is
 * graceful: the switcher stays on the old tenant and no UI breaks.
 */

import { create } from 'zustand';
import { api } from './api/client';
import { supabase } from './supabase';
import { queryClient } from './query-client';
import { signOutAndRedirect } from './sign-out';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface Tenant {
  id: string;
  slug: string;
  name: string;
}

/** What `switchTenant` reports back. A switch is only `ok` once the claim has
 *  been re-stamped by the backend AND the session carrying it re-minted. */
export interface SwitchTenantResult {
  ok: boolean;
  error?: string;
}

interface TenantState {
  activeTenantId: string | null;
  tenants: Tenant[];
  /** Why the last switch failed, or null. Cleared at the start of every
   *  attempt and on success. */
  switchError: string | null;
  /**
   * Replace the tenant list (called by the route loader that fetches
   * /api/v1/me/tenants). Reconciles the persisted activeTenantId — if it's
   * no longer in the list, the first tenant becomes active.
   */
  setTenants: (tenants: Tenant[]) => void;
  /**
   * Switch to a different tenant. POSTs to the backend to re-scope the JWT;
   * on success updates activeTenantId locally. On failure the local state
   * is unchanged so the UI stays consistent with the backend.
   */
  switchTenant: (id: string) => Promise<SwitchTenantResult>;
}

/** What POST /v1/auth/switch-tenant answers (the fields this store reads). */
interface SwitchTenantResponse {
  switched?: boolean;
  alreadyActive?: boolean;
  tenantId?: string;
}

/**
 * Re-stamp the claim to `tenantId`. Returns true only when the server says the
 * LIVE claim now names that tenant; never throws, because its callers are
 * already handling a failure.
 *
 * QA P2-8 (2026-10-04): this used to return true for any 2xx. The rollback is
 * sent with the OLD token, whose claim already names `tenantId`, and the server
 * used to compare against that token, answer `switched: false` and write
 * nothing, so a "successful" rollback left GoTrue on the new workspace. The
 * server now compares against the live claim (`switched: true` means it wrote,
 * `alreadyActive: true` means the live claim already named it), and this reads
 * the answer instead of assuming it.
 */
async function restampTenant(tenantId: string): Promise<boolean> {
  try {
    const res = await api<SwitchTenantResponse>('/api/v1/auth/switch-tenant', {
      method: 'POST',
      body: { tenantId },
    });
    return (
      res?.tenantId === tenantId && (res.switched === true || res.alreadyActive === true)
    );
  } catch {
    return false;
  }
}

/** `signOutAndRedirect` takes the router's `navigate`; there is no router in a
 *  store, so this is the equivalent hard navigation. A full load is fine here:
 *  the session is being torn down anyway. */
const redirectToLogin = (): void => {
  window.location.assign('/auth/login');
};

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      activeTenantId: null,
      tenants: [],
      switchError: null,

      setTenants: (tenants) => {
        const current = get().activeTenantId;
        const valid = current !== null && tenants.some((t) => t.id === current);
        set({
          tenants,
          activeTenantId: valid ? current : (tenants[0]?.id ?? null),
        });
      },

      // 2026-08-06: this was written against an endpoint that did not exist, and
      // it was wrong in two ways that only became visible once it did.
      //
      //   1. It sent `credentials: 'include'` and no Authorization header. The
      //      API authenticates with a Supabase BEARER token, not a cookie, so
      //      every call would have 401'd. `api()` attaches the token.
      //   2. It set `activeTenantId` and stopped. The workspace lives in the JWT
      //      claim, and the token in hand still carries the OLD one until it is
      //      re-minted — so the UI would have said "switched" while every
      //      subsequent request went to the previous workspace. The backend
      //      returns `sessionRefreshRequired` precisely to say so.
      //
      // Order matters: refresh FIRST, then move the local pointer. If the
      // refresh fails we stay put AND put the server-side claim back, because
      // a UI pointing at a workspace the token cannot open is worse than not
      // switching at all, and a claim pointing at a workspace the UI is not
      // showing is worse than both (see the rollback block below).
      // 2026-08-08: ...and it was still wrong in a third way. It re-scoped the
      // JWT and left the ENTIRE QUERY CACHE holding the previous workspace's
      // data — trusts, clients, dashboard, documents, all of it. React Query
      // serves cached entries for `staleTime` (30s) without refetching, so the
      // first seconds after a switch showed the OLD firm's book under the NEW
      // firm's name. In a product whose whole promise is tenant isolation, that
      // is the worst possible screen to render.
      //
      // Every other principal change in this app already drops the cache:
      // sign-out.ts:49, firm-act-bar.tsx:62 (exiting a client),
      // impersonation-banner.tsx:53, onboarding-reset.ts. Switching workspace is
      // the same class of event and had simply been missed. This uses the SAME
      // mechanism rather than inventing a sixth dialect for it.
      //
      // ⚠️ The clear happens AFTER the refresh and BEFORE the pointer moves, so
      // there is no window in which a component can observe the new tenant id
      // while stale rows are still cached. The `['me','tenants']` list is
      // dropped along with everything else, which matters: `useMyTenants` holds
      // it for 5 minutes, and a stale copy would let `useSyncMyTenants` re-adopt
      // the PRE-switch workspace as `current` seconds after the switch landed.
      //
      // The proper fix for this class is to put the principal in the query key
      // so no purge is needed at all — tracked separately; it is a larger change
      // with real UX cost (a keyed provider remounts the whole app, which would
      // also fire on every act-as change).
      //
      // 2026-09-21 (ELR-08): ...and a fourth. `refreshSession()` RESOLVES with
      // `{ data, error }` on failure, it does not throw, so the catch below
      // never saw a failed refresh even though its own comment said it did. The
      // cache was dropped and the pointer moved on a token that had not been
      // re-minted: the UI named the new workspace, the claim still named the
      // old one, and every request went to the old firm with an empty cache
      // underneath. The error is now read, and reported.
      switchTenant: async (id) => {
        set({ switchError: null });
        const previous = get().activeTenantId;
        try {
          await api('/api/v1/auth/switch-tenant', {
            method: 'POST',
            body: { tenantId: id },
          });
        } catch (err) {
          // 404 MEMBERSHIP_NOT_FOUND, 502 CLAIM_PROMOTION_FAILED, or a network
          // failure. Leave activeTenantId unchanged AND the cache intact, so
          // the UI keeps matching the live token.
          const message = err instanceof Error ? err.message : 'Could not switch workspace.';
          set({ switchError: message });
          return { ok: false, error: message };
        }

        const { error } = await supabase.auth.refreshSession();
        if (error) {
          // ── ROLL THE CLAIM BACK (2026-09-21, fix round 2) ─────────────────
          // Staying put is not enough on its own. The POST above ALREADY
          // re-stamped `app_metadata.tenant_id` server side, so the claim now
          // names the new workspace whatever this client does. The token in
          // hand still names the old one, and supabase refreshes it on its own
          // schedule: minutes later the session silently becomes the NEW
          // workspace while the header, the cache and `activeTenantId` all
          // still say the old firm. That is the cross-tenant screen the block
          // above exists to prevent, arriving late instead of immediately.
          //
          // So put the claim back where the UI says it is. If THAT fails too,
          // the client can no longer tell which workspace the next token will
          // open, and the only safe state is no session at all.
          const message = error.message || 'Could not refresh your session.';
          const rolledBack = previous !== null && (await restampTenant(previous));
          if (!rolledBack) {
            set({ switchError: message });
            await signOutAndRedirect(redirectToLogin);
            return { ok: false, error: message };
          }
          set({ switchError: message });
          return { ok: false, error: message };
        }

        queryClient.clear();
        set({ activeTenantId: id });
        return { ok: true };
      },
    }),
    {
      name: 'elreign-active-tenant',
      storage: createJSONStorage(() => localStorage),
      // Only persist activeTenantId — the tenants list is re-hydrated by the
      // route loader on every session and would otherwise go stale.
      partialize: (state) => ({ activeTenantId: state.activeTenantId }),
    },
  ),
);
